import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
	Hash,
	Clock,
	CheckCircle2,
	XCircle,
	User,
	Cpu,
	Database,
	Fuel,
	Wallet,
	Box
} from 'lucide-react'

import { StatsCard } from '@/components/shared/StatsCard'
import { formatUnixTimestamp, formatBalanceInSUI, formatBalanceInMIST } from '@/utils/format';

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { useTransactionBlockDetails } from '@/hooks/useApi'

export const TransactionBlockDetailsPage = () => {
	const { digest } = useParams<{ digest: string }>()
	if (!digest) {
		return <> Not Found </>
	}

	const { data: transactionBlockDetails } = useTransactionBlockDetails(digest);

	const totalGasCost = useMemo(() => {
		if (!transactionBlockDetails) return 0;

		return (
			Number(transactionBlockDetails?.gasUsage.computationCost) +
			Number(transactionBlockDetails?.gasUsage.storageCost) -
			Number(transactionBlockDetails?.gasUsage.storageRebate)
		);
	}, [transactionBlockDetails])



	if (!transactionBlockDetails) {
		return <> Loading... </>;
	}

	return (
		<div className="flex flex-col overflow-hidden w-full h-full">
			<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
			>
				<div className="flex flex-col w-full h-full gap-6 px-2 lg:px-4 pt-4">
					<h1 className="scroll-m-20 text-md lg:text-2xl font-bold tracking-tight">
						Transaction Block Details
					</h1>

					<div className='grid grid-cols-1 md:grid-cols-5 gap-2'>
						<div className='md:col-span-4'>
							<StatsCard
								title="Digest"
								className='text-sm'
								valueStyleOverride='text-lg lg:text-2xl overflow-hidden whitespace-nowrap text-ellipsis'
								value={transactionBlockDetails?.digest}
								icon={<Hash className="text-primary w-4 h-4" />}
							/>
						</div>
						<div className='md:col-span-1'>
							<StatsCard
								title="Status"
								className='text-sm'
								valueStyleOverride='text-lg lg:text-2xl overflow-hidden whitespace-nowrap text-ellipsis'
								value={transactionBlockDetails?.status}
								icon={
									transactionBlockDetails?.status === 'success' ? (
										<CheckCircle2 className="text-primary w-4 h-4" />
									) : (
										<XCircle className="text-primary w-4 h-4" />
									)
								}
							/>
						</div>
					</div>
					<div className='grid grid-cols-1 lg:grid-cols-5 gap-2'>
						{/* Metadata */}
						<div className='lg:col-span-2'>
							<div className='flex flex-col gap-2'>
								<StatsCard
									title="Checkpoint"
									className='text-sm'
									valueStyleOverride='text-md xl:text-xl'
									value={Number(transactionBlockDetails?.sequenceNumber).toLocaleString()}
									icon={<Box className='h-4 w-4'/>}
								/>
								<StatsCard
									title="Timestamp"
									className='text-sm'
									valueStyleOverride='text-md xl:text-xl'
									value={formatUnixTimestamp(Number(transactionBlockDetails?.timestampMS))}
									icon={<Clock className='h-4 w-4'/>}
								/>
								<StatsCard
									title="Sender"
									className='text-sm'
									valueStyleOverride='text-md xl:text-xl'
									value={
										<Link to={`/accounts/${transactionBlockDetails.sender}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
											{transactionBlockDetails.sender.slice(0, 8)}...{transactionBlockDetails.sender.slice(-6)}
										</Link>
									}
									icon={<User className="text-primary h-4 w-4" />}
								/>
							</div>
						</div>
						{/* Gas Usage */}
						<div className='lg:col-span-3'>
							<Card className='bg-transparent'>
								<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
									<div className='flex flex-row items-center px-2 pt-2'>
										<div className="rounded-full px-2 transition-colors">
											<Fuel className='h-4 w-4' />
										</div>
										<div className='flex-1'>
											<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Gas Usage</CardTitle>
										</div>
									</div>
									<div className='px-4'>
										<CardDescription className='text-xs lg:text-md'>
											Gas cost breakdown for this transaction
										</CardDescription>
									</div>
								</CardHeader>
								<CardContent className='pt-4'>
									<div className='flex flex-col gap-3'>
										<div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
											<StatsCard
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												title="Total Cost"
												value={
													<>
														<div className='text-md'>
															{totalGasCost ? formatBalanceInSUI(Number(totalGasCost)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{totalGasCost ? formatBalanceInMIST(Number(totalGasCost)) : '--'} MIST
														</div>
													</>
												}
												icon={<Wallet className='h-5 w-5' />}
											/>
											<StatsCard
												title="Computation Cost"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{transactionBlockDetails?.gasUsage?.computationCost ? formatBalanceInSUI(Number(transactionBlockDetails?.gasUsage?.computationCost)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{transactionBlockDetails?.gasUsage?.computationCost ? formatBalanceInMIST(Number(transactionBlockDetails?.gasUsage?.computationCost)) : '--'} MIST
														</div>
													</>
												}
												icon={<Cpu className='h-5 w-5' />}
											/>
										</div>
										<div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
											<StatsCard
												title="Storage Cost"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{transactionBlockDetails?.gasUsage?.storageCost ? formatBalanceInSUI(Number(transactionBlockDetails?.gasUsage?.storageCost)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{transactionBlockDetails?.gasUsage?.storageCost ? formatBalanceInMIST(Number(transactionBlockDetails?.gasUsage?.storageCost)) : '--'} MIST
														</div>
													</>
												}
												icon={<Database className='h-5 w-5' />}
											/>
											<StatsCard
												title="Storage Rebate"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{transactionBlockDetails?.gasUsage?.storageRebate ? formatBalanceInSUI(Number(transactionBlockDetails?.gasUsage?.storageRebate)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{transactionBlockDetails?.gasUsage?.storageRebate ? formatBalanceInMIST(Number(transactionBlockDetails?.gasUsage?.storageRebate)) : '--'} MIST
														</div>
													</>
												}
												icon={<Database className='h-5 w-5' />}
											/>
											<StatsCard
												title="Non-Refundable Fee"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{transactionBlockDetails?.gasUsage?.nonRefundableStorageFee ? formatBalanceInSUI(Number(transactionBlockDetails?.gasUsage?.nonRefundableStorageFee)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{transactionBlockDetails?.gasUsage?.nonRefundableStorageFee ? formatBalanceInMIST(Number(transactionBlockDetails?.gasUsage?.nonRefundableStorageFee)) : '--'} MIST
														</div>
													</>
												}
												icon={<Database className='h-5 w-5' />}
											/>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				</div>
			</div>

		</div>
	)
}